<template>
  <div class="process-configuration-manager">
    <div class="process-selector">
      <el-row :gutter="16">
        <el-col :span="18">
          <el-form-item label="Seleccionar proceso">
            <el-select
              v-model="procesoSeleccionado"
              filterable
              clearable
              placeholder="Selecciona un proceso"
            >
              <el-option
                v-for="proceso in procesosDisponibles"
                :key="proceso.Codigo"
                :label="proceso.Descripcion"
                :value="proceso.Codigo"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col
          :span="6"
          class="button-col"
        >
          <el-button
            type="primary"
            :disabled="!procesoSeleccionado"
            @click="agregarProceso"
          >
            + Agregar proceso
          </el-button>
        </el-col>
      </el-row>
    </div>

    <div
      v-if="orphans.length > 0"
      class="orphans-zone"
    >
      <div class="orphans-title">
        <el-icon><Warning /></el-icon>
        Procesos sin Sección asignada (Requiere configuración)
      </div>
      <draggable
        v-model="orphans"
        item-key="_uid"
        handle=".drag-handle"
        animation="200"
        class="draggable-list"
        @change="onDragChange"
      >
        <template #item="{ element: proceso }">
          <ProcessConfigurationItem
            ref="processItems"
            :model-value="proceso"
            :proceso="getProcesoInfo(proceso.CodigoProceso)"
            :index="getIndex(proceso._uid)"
            :procesos-disponibles="procesosDisponibles"
            :secciones-disponibles="getSeccionesDisponiblesLocal(proceso)"
            :recursos-disponibles="recursosDisponibles"
            :categorias-disponibles="categoriasDisponibles"
            :procesos-con-categorias="procesosConCategorias"
            :materiales-disponibles="materialesDisponibles"
            :tiene-error="procesosConError.has(proceso._uid) || !proceso.CodigoSeccion"
            @update:model-value="updateProcess(proceso._uid, $event)"
            @remove="removeProcessByUid(proceso._uid)"
            @material-changed="onMaterialChanged"
          />
        </template>
      </draggable>
    </div>

    <div
      v-for="sec in sectionsData"
      :key="sec.Codigo"
      class="section-zone"
    >
      <div
        class="section-title"
        @click="toggleSection(sec.Codigo)"
      >
        <el-icon
          class="collapse-icon"
          :class="{ 'is-collapsed': collapsedSections.has(sec.Codigo) }"
        >
          <ArrowDown />
        </el-icon>
        <span>{{ sec.Orden }}. {{ sec.Descripcion }}</span>
      </div>

      <div
        v-show="!collapsedSections.has(sec.Codigo)"
        class="section-content"
      >
        <div class="group-title">Procesos Por Defecto</div>
        <draggable
          v-model="sec.listTrue"
          item-key="_uid"
          handle=".drag-handle"
          animation="200"
          class="draggable-list"
          @change="onDragChange"
        >
          <template #item="{ element: proceso }">
            <ProcessConfigurationItem
              :model-value="proceso"
              :proceso="getProcesoInfo(proceso.CodigoProceso)"
              :index="getIndex(proceso._uid)"
              :procesos-disponibles="procesosDisponibles"
              :secciones-disponibles="getSeccionesDisponiblesLocal(proceso)"
              :recursos-disponibles="recursosDisponibles"
              :categorias-disponibles="categoriasDisponibles"
              :procesos-con-categorias="procesosConCategorias"
              :materiales-disponibles="materialesDisponibles"
              :tiene-error="procesosConError.has(proceso._uid)"
              @update:model-value="updateProcess(proceso._uid, $event)"
              @remove="removeProcessByUid(proceso._uid)"
              @material-changed="onMaterialChanged"
            />
          </template>
          <template #footer>
            <div
              v-if="sec.listTrue.length === 0"
              class="empty-group"
            >
              No hay procesos obligatorios en esta sección
            </div>
          </template>
        </draggable>

        <div
          class="group-title"
          style="margin-top: 24px"
        >
          Procesos Opcionales
        </div>
        <draggable
          v-model="sec.listFalse"
          item-key="_uid"
          handle=".drag-handle"
          animation="200"
          class="draggable-list"
          @change="onDragChange"
        >
          <template #item="{ element: proceso }">
            <ProcessConfigurationItem
              :model-value="proceso"
              :proceso="getProcesoInfo(proceso.CodigoProceso)"
              :index="getIndex(proceso._uid)"
              :procesos-disponibles="procesosDisponibles"
              :secciones-disponibles="getSeccionesDisponiblesLocal(proceso)"
              :recursos-disponibles="recursosDisponibles"
              :categorias-disponibles="categoriasDisponibles"
              :procesos-con-categorias="procesosConCategorias"
              :materiales-disponibles="materialesDisponibles"
              :tiene-error="procesosConError.has(proceso._uid)"
              @update:model-value="updateProcess(proceso._uid, $event)"
              @remove="removeProcessByUid(proceso._uid)"
              @material-changed="onMaterialChanged"
            />
          </template>
          <template #footer>
            <div
              v-if="sec.listFalse.length === 0"
              class="empty-group"
            >
              No hay procesos opcionales en esta sección
            </div>
          </template>
        </draggable>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  watch,
  nextTick,
} from 'vue';
import {
  ElMessage,
  ElMessageBox,
} from 'element-plus';
import {
  Warning,
  ArrowDown,
} from '@element-plus/icons-vue';
import ProcessConfigurationItem from './process_configuration_item.vue';
import draggable from 'vuedraggable';

// Props
const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  procesosConCategoria: { type: Array, default: () => [] },
  procesosDisponibles: { type: Array, default: () => [] },
  todosLosProcesos: { type: Array, default: () => [] },
  seccionesDisponibles: { type: Array, default: () => [] },
  getSeccionesDisponiblesParaProceso: { type: Function, default: null },
  recursosDisponibles: { type: Array, default: () => [] },
  categoriasDisponibles: { type: Array, default: () => [] },
  procesosConCategorias: { type: Array, default: () => [] },
  materialesDisponibles: { type: Map, default: () => new Map() },
  procesosConError: { type: Set, default: () => new Set() },
});

const emit = defineEmits(['update:modelValue', 'material-changed']);

// Estado local
const procesoSeleccionado = ref(null);

const collapsedSections = ref(new Set());

const toggleSection = (codigoSeccion) => {
  if (collapsedSections.value.has(codigoSeccion)) {
    collapsedSections.value.delete(codigoSeccion);
  } else {
    collapsedSections.value.add(codigoSeccion);
  }
};

const orphans = ref([]);
const sectionsData = ref([]);
let isInternalUpdate = false;

const syncFromProps = () => {
  if (isInternalUpdate) return;

  orphans.value = [];
  const map = new Map();

  const sortedSecs = [...props.seccionesDisponibles].sort(
    (a, b) => (a.Orden || 0) - (b.Orden || 0),
  );

  sortedSecs.forEach((s) => {
    map.set(s.Codigo, {
      ...s,
      listTrue: [],
      listFalse: [],
    });
  });

  props.modelValue.forEach((p) => {
    if (!p.CodigoSeccion) {
      orphans.value.push(p);
    } else if (map.has(p.CodigoSeccion)) {
      const sec = map.get(p.CodigoSeccion);
      if (p.PorDefecto) sec.listTrue.push(p);
      else sec.listFalse.push(p);
    } else {
      orphans.value.push(p);
    }
  });

  sectionsData.value = Array.from(map.values()).filter(
    (sec) => sec.listTrue.length > 0 || sec.listFalse.length > 0,
  );
};

watch(
  () => props.modelValue,
  () => {
    // isInternalUpdate se resetea en su propio nextTick (ver emitFlatArray);
    // hay que leerlo ahora (fase pre-flush) para no perder la guarda cuando
    // el reset corra antes que nuestro propio callback diferido.
    if (isInternalUpdate) return;

    // Diferido a nextTick: si el cambio viene de un el-switch que mueve el
    // proceso entre listTrue/listFalse (desmontando su item en este mismo
    // flush), regrupar de forma síncrona pisa el nextTick interno de
    // el-switch (input.value.checked) y explota con "Cannot set properties
    // of null". Difiriendo un tick, ese callback corre primero contra el
    // <input> todavía montado.
    nextTick(() => {
      syncFromProps();
    });
  },
  { deep: true, immediate: true },
);

const uid = () => Math.random().toString(36).slice(2, 9);
const getIndex = (uid) => props.modelValue.findIndex((p) => p._uid === uid);

const getProcesoInfo = (codigoProceso) => {
  const procesoCompleto = props.todosLosProcesos.find((p) => p.Codigo === codigoProceso);
  if (procesoCompleto)
    return { Codigo: procesoCompleto.Codigo, Descripcion: procesoCompleto.Descripcion };

  const proceso = props.procesosDisponibles.find((p) => p.Codigo === codigoProceso);
  if (proceso) return { Codigo: proceso.Codigo, Descripcion: proceso.Descripcion };

  return { Codigo: codigoProceso, Descripcion: `Proceso ${codigoProceso}` };
};

const getSeccionesDisponiblesLocal = (proceso) => {
  if (props.getSeccionesDisponiblesParaProceso) {
    return props.getSeccionesDisponiblesParaProceso(proceso.CodigoProceso, proceso.CodigoSeccion);
  }
  return props.seccionesDisponibles;
};

const updateProcess = (uid, newValue) => {
  const flat = [...props.modelValue];
  const index = flat.findIndex((p) => p._uid === uid);
  if (index !== -1) {
    flat[index] = newValue;
    emit('update:modelValue', flat);
  }
};

const emitFlatArray = () => {
  isInternalUpdate = true;
  const newFlat = [];

  newFlat.push(...orphans.value);

  const sortedSecs = [...props.seccionesDisponibles].sort(
    (a, b) => (a.Orden || 0) - (b.Orden || 0),
  );
  sortedSecs.forEach((s) => {
    const sec = sectionsData.value.find((sd) => sd.Codigo === s.Codigo);
    if (sec) {
      newFlat.push(...sec.listTrue);
      newFlat.push(...sec.listFalse);
    }
  });

  emit('update:modelValue', newFlat);

  nextTick(() => {
    isInternalUpdate = false;
  });
};

const onDragChange = () => {
  emitFlatArray();
};

const removeProcessByUid = async (uid) => {
  try {
    await ElMessageBox.confirm('¿Está seguro de eliminar este proceso?', 'Confirmar eliminación', {
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      type: 'warning',
    });
    const flat = props.modelValue.filter((p) => p._uid !== uid);
    emit('update:modelValue', flat);
    ElMessage.success('Proceso eliminado correctamente');
  } catch {}
};

const agregarProceso = () => {
  if (!procesoSeleccionado.value) {
    ElMessage.warning('Selecciona un proceso primero');
    return;
  }

  const procesoData = props.procesosDisponibles.find((p) => p.Codigo === procesoSeleccionado.value);

  const nuevaConfiguracion = {
    _uid: uid(),
    CodigoProceso: procesoData.Codigo,
    CodigoSeccion: null,
    PorDefecto: false,
    AdmiteSuaje: false,
    CodigoRecurso: null,
    codigosMateriales: [],
    materialesPorCategoria: {},
  };

  const newFlat = [...props.modelValue, nuevaConfiguracion];
  emit('update:modelValue', newFlat);
  procesoSeleccionado.value = null;
  ElMessage.success('Proceso agregado. Por favor, asígnele una sección.');
};

const onMaterialChanged = (data) => {
  emit('material-changed', data);
};

defineExpose({
  inicializarEstadoColapso: () => {
    // Ya no es necesario
  },
});
</script>

<style scoped>
.process-configuration-manager {
  width: 100%;
}

.process-selector {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.button-col {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding-bottom: 8px;
}

.orphans-zone {
  border: 2px dashed #f56c6c;
  background-color: #fef0f0;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
}

.orphans-title {
  color: #f56c6c;
  font-weight: 600;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Transiciones */
.list-enter-active,
.list-leave-active {
  transition: all 0.16s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.section-zone {
  margin-bottom: 24px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 16px;
  background-color: #ffffff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #303133;
  background-color: #f5f7fa;
  margin: -16px -16px 16px -16px;
  padding: 12px 16px;
  border-bottom: 1px solid #e4e7ed;
  border-left: 5px solid #409eff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.section-title:hover {
  background-color: #ebeef5;
}

.collapse-icon {
  margin-right: 8px;
  font-size: 16px;
  color: #909399;
  transition: transform 0.2s ease;
}

.collapse-icon.is-collapsed {
  transform: rotate(-90deg);
}

.group-title {
  font-size: 12px;
  font-weight: bold;
  color: #909399;
  margin-top: 12px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.empty-group {
  padding: 12px;
  text-align: center;
  color: #c0c4cc;
  background-color: #f8f9fa;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  font-size: 13px;
}
</style>
